import React, { useState, useEffect } from "react";

// --- Timer Logic - Fixed End Date for All Users ---
// New Year Sale ends Dec 31, 2026 23:59:59 IST
const OFFER_END_DATE = new Date("2026-12-31T23:59:59+05:30").getTime();

function getStoredEndTime() {
  if (typeof window === "undefined") return OFFER_END_DATE; // SSR safe
  return OFFER_END_DATE; // Same end time for all users
}

function getTimeLeft(endTime) {
  const now = Date.now();
  const diff = Math.max(endTime - now, 0);

  return {
    days: Math.floor(diff / (24 * 60 * 60 * 1000)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

// --- New Year Themed Component ---
export default function NewYearSaleOffer({ priceBasic }) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [endTime, setEndTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [featuresArray, setFeaturesArray] = useState([]);

  const router = require("next/router").useRouter();
  const Cookies = require("js-cookie");

  useEffect(() => {
    setMounted(true);

    const et = getStoredEndTime();
    setEndTime(et);
    setVisible(true);
    setTimeLeft(getTimeLeft(et));

    async function fetchPricing() {
      try {
        const API_URL = process.env.NEXT_PUBLIC_API_URL || "";
        if (!API_URL) return;

        const axios = require("axios");
        const res = await axios.get(`${API_URL}/api/fetch-pgservices-data`);

        if (res?.data?.data?.length > 0) {
          const fetched = res.data.data[0];

          const sectionOrder = ["tools", "analysis", "postExamMentorship"];
          const allSections = Object.keys(fetched.features || {}).filter(
            (s) => s !== "lastUpdatedBy" && s !== "_id"
          );

          const orderedSections = [
            ...sectionOrder.filter((s) => allSections.includes(s)),
            ...allSections.filter((s) => !sectionOrder.includes(s)),
          ];

          const arr = [];
          orderedSections.forEach((section) => {
            const sectionFeatures = fetched.features[section] || {};
            Object.keys(sectionFeatures).forEach((featureKey) => {
              if (sectionFeatures[featureKey]["pgbasic"] === "Yes") {
                arr.push(
                  String(featureKey)
                    .replace(/([a-z])([A-Z])/g, "$1 $2")
                    .replace(/[_-]/g, " ")
                    .replace(/&/g, " & ")
                    .trim()
                );
              }
            });
          });

          setFeaturesArray(arr);
        }
      } catch (err) {
        // no fallback
      }
    }

    fetchPricing();
  }, []);

  const handleGetStarted = (e) => {
    e.preventDefault();
    const tier = "pgbasic";
    const couponCode = "NS500";
    const authToken = Cookies.get("authToken");

    const isLoggedIn =
      authToken &&
      authToken !== "undefined" &&
      authToken !== "null" &&
      authToken.trim() !== "";

    const checkoutQuery = `tier=${encodeURIComponent(tier)}&price=${encodeURIComponent(
      priceBasic
    )}&features=${encodeURIComponent(JSON.stringify(featuresArray))}&coupon=${encodeURIComponent(couponCode)}&couponSource=${encodeURIComponent('blackfriday')}`;

    const checkoutPath = `/checkout?${checkoutQuery}`;

    if (!isLoggedIn) {
      try {
        sessionStorage.setItem(
          "checkoutAfterLogin",
          JSON.stringify({ tier, price: priceBasic, features: featuresArray, coupon: couponCode, couponSource: 'blackfriday' })
        );
      } catch (e) {}

      const loginHost = "https://portal.neetsupport.com";
      const returnUrl = window.location.origin + checkoutPath;
      const loginUrl = `${loginHost}/login?next=${encodeURIComponent(returnUrl)}`;
      window.location.href = loginUrl;
      return;
    }

    router.push({
      pathname: "/checkout",
      query: {
        tier: tier,
        price: priceBasic,
        features: JSON.stringify(featuresArray),
        coupon: couponCode,
        couponSource: 'blackfriday',
      },
    });
  };

  useEffect(() => {
    if (!mounted || !visible || !endTime) return;

    const interval = setInterval(() => {
      const tl = getTimeLeft(endTime);
      setTimeLeft(tl);

      if (tl.days === 0 && tl.hours === 0 && tl.minutes === 0 && tl.seconds === 0) {
        setVisible(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [mounted, visible, endTime]);

  const originalPrice = 4980;
  const savingAmount = 500;

  if (!mounted || !visible) return null;

  // --- New Year theme (purple + gold) ---
  const nyBg = "bg-[#6d28d9]"; // purple
  const nyText = "text-[#fbbf24]"; // gold
  const nyBorder = "border-[#fbbf24]/40";
  const nyShadow = "shadow-lg shadow-[#6d28d9]/40";
  const nyHover = "hover:bg-[#5b21b6]"; // darker purple

  return (
    <div className="fixed bottom-2 left-2 right-2 sm:right-6 sm:left-auto z-[100] mb-4 sm:mb-8">
      {/* Close Button */}
      <button
        onClick={() => setVisible(false)}
        className="absolute top-[-18px] right-[-10px] w-7 h-7 sm:w-8 sm:h-8 rounded-full text-white bg-gray-700 hover:bg-gray-600 transition-colors flex items-center justify-center text-lg sm:text-xl font-light z-[101] shadow-md"
        aria-label="Close offer"
      >
        &times;
      </button>

      <div
        className={`
          max-w-xs sm:max-w-md md:max-w-lg lg:max-w-xl w-full ml-auto
          bg-[#101317] ${nyBorder} border rounded-2xl shadow-2xl shadow-black/50
          px-3 py-3 sm:px-8 sm:py-6
          flex flex-col sm:flex-row sm:items-center sm:justify-between
          gap-4 sm:gap-8
          animate-fade-in
          relative
        `}
      >
        {/* LEFT */}
        <div className="flex-1 flex flex-col gap-2 sm:gap-3 pr-0 sm:pr-6">
          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
            <span
              className={`${nyBg} text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-extrabold uppercase tracking-widest shadow-md`}
            >
              🎉 NEW YEAR OFFER
            </span>
            <span
              className={`bg-[#fbbf24]/10 ${nyText} px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[9px] sm:text-[11px] font-semibold uppercase tracking-wide border border-[#fbbf24]/30`}
            >
              NEET PG OFFER
            </span>
          </div>

          <p className="text-xs sm:text-lg font-bold text-white leading-tight mt-1">
            New Year Special on NEET PG counselling access
          </p>

          <div className="flex items-center gap-2 sm:gap-3 mt-1">
            <div className="flex items-baseline gap-1 sm:gap-2">
              <span className={`text-xl sm:text-3xl font-black ${nyText} leading-none`}>
                ₹{priceBasic.toLocaleString()}
              </span>
            </div>

            <span className="inline-flex items-center gap-1 bg-green-500/10 text-green-400 text-[10px] sm:text-sm font-bold px-2 py-1 sm:px-3 sm:py-1.5 rounded-full whitespace-nowrap border border-green-500/30">
              Save ₹{savingAmount.toLocaleString()}
            </span>
          </div>

          {/* Optional tiny celebratory line */}
          <p className="text-[10px] sm:text-xs text-gray-400">
            Limited-time New Year pricing. Unlock your plan before the timer hits zero.
          </p>
        </div>

        {/* RIGHT */}
        <div className="w-full sm:w-64 flex flex-col items-stretch gap-2 sm:gap-3 mt-2 sm:mt-0">
          <div className="bg-black/40 rounded-xl p-2 sm:p-3 border border-[#fbbf24]/30">
            <div className="text-[9px] sm:text-[10px] text-[#fbbf24] text-center font-bold uppercase mb-1 sm:mb-2 tracking-widest">
              Ends In
            </div>

            <div className="flex justify-center gap-1 sm:gap-2">
              {[
                { label: "Days", value: timeLeft.days },
                { label: "Hrs", value: timeLeft.hours },
                { label: "Min", value: timeLeft.minutes },
                { label: "Sec", value: timeLeft.seconds },
              ].map((item) => (
                <div key={item.label} className="flex flex-col items-center mx-0.5 sm:mx-1">
                  <span className="text-base sm:text-xl font-extrabold text-white bg-gray-800 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-md min-w-[22px] sm:min-w-[30px] text-center">
                    {String(item.value).padStart(2, "0")}
                  </span>
                  <small className="text-[8px] sm:text-[10px] text-gray-400 uppercase mt-0.5 sm:mt-1">
                    {item.label}
                  </small>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleGetStarted}
            className={`
              w-full
              ${nyBg} text-white
              px-3 py-2 sm:px-4 sm:py-3 rounded-xl
              font-extrabold text-xs sm:text-base uppercase
              ${nyShadow}
              ${nyHover}
              active:scale-[0.99] transition-all duration-150
            `}
            aria-label="Get New Year Offer"
          >
            Get New Year Offer
          </button>
        </div>
      </div>
    </div>
  );
}
